import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SecureStorageService } from './secure-storage.service';

/**
 * THE LIVE CHANNEL — the gateway's own WebSocket, in place of Pusher.
 *
 * This panel used Pusher for one thing: a `sessions_termination` event that asked the browser
 * to log itself out. Two problems with that arrangement, both now gone: the event went to a
 * channel EVERY panel subscribed to, so every admin's browser received every termination and
 * was trusted to check whether it was the one being kicked; and the app secret lived in the
 * source of a public bundle.
 *
 * The gateway authenticates the socket with this session's token and delivers only what is
 * addressed to this account — so a termination frame arriving here IS for this admin, and a
 * notification frame is one this admin is a recipient of.
 *
 * OneSignal is not involved: that is for phones, where a notification has to arrive with the
 * app closed. A browser with the page open needs a socket, not a push service.
 */
@Injectable({
  providedIn: 'root',
})
export class RealtimeService {
  private socket: WebSocket | null = null;
  private ping: any = null;
  private retryTimer: any = null;
  private retries = 0;
  /** true once a disconnect was asked for, so a close does not reconnect. */
  private closed = true;

  /**
   * Every notification frame for this admin. The layout subscribes to refresh its unread
   * count; nothing has to poll to find out that something arrived.
   */
  readonly notifications$ = new Subject<{ event: string; data: any }>();

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private secureStorage: SecureStorageService,
  ) {}

  /**
   * Open the channel.
   *
   * Nothing to configure — no key, no cluster, no channel name. The endpoint is the gateway
   * this panel already talks to, and the identity is the session token.
   */
  connect(): void {
    if (this.socket) return;
    this.closed = false;
    this.open();
  }

  private url(): string | null {
    const token = this.secureStorage.getItem('authToken') || '';
    if (!token) return null; // signed out — nothing to listen as

    // loyaltyApiUrl is the /action endpoint; the socket lives beside it on the same origin.
    const origin = String(environment.loyaltyApiUrl || '')
      .replace(/\/action\/?$/i, '')
      .replace(/\/+$/, '');
    if (!origin) return null;
    const ws = origin.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
    return ws + '/ws?token=' + encodeURIComponent(token);
  }

  private open(): void {
    const url = this.url();
    if (!url) return;

    try {
      const socket = new WebSocket(url);
      this.socket = socket;

      socket.onopen = () => {
        this.retries = 0;
        // Proxies drop an idle WebSocket long before anything notices, and a dead socket looks
        // exactly like a quiet one. The ping is what tells them apart.
        this.ping = setInterval(() => {
          try {
            socket.send(JSON.stringify({ type: 'ping' }));
          } catch {
            /* the close handler reconnects */
          }
        }, 25000);
      };

      socket.onmessage = (ev: MessageEvent) => {
        let frame: any;
        try {
          frame = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (frame?.type !== 'notification') return;

        if (frame.event === 'sessions_termination') {
          this.handleSessionTermination(frame.data);
          return;
        }
        this.notifications$.next({ event: String(frame.event || 'admin'), data: frame.data });
      };

      socket.onclose = () => {
        this.clear();
        if (this.closed) return; // closed on purpose
        // Capped backoff, so a gateway restart does not become a reconnect flood.
        this.retries = Math.min(this.retries + 1, 6);
        const delay = Math.min(1000 * Math.pow(2, this.retries - 1), 30000);
        this.retryTimer = setTimeout(() => this.open(), delay);
      };

      socket.onerror = () => {
        // onclose always follows and owns the reconnect.
      };
    } catch (error) {
      console.error('Failed to open the live channel:', error);
    }
  }

  /**
   * This account's sessions were ended by another admin.
   *
   * No id comparison any more: the gateway sends this frame only to the sockets of the account
   * being terminated, and the tokens are revoked server-side regardless — so this is the
   * courtesy of getting out of the way, not the security boundary it used to pretend to be.
   */
  private handleSessionTermination(_data: any): void {
    this.forceLogout('Your session has been terminated by an administrator.');
  }

  private forceLogout(message: string): void {
    this.secureStorage.removeItem('authToken');
    this.secureStorage.removeItem('id');
    this.secureStorage.removeItem('userDetails');
    this.toastr.warning(message, 'Session Terminated');
    this.disconnect();
    this.router.navigate(['/auth/login']);
  }

  private clear(): void {
    if (this.ping) {
      clearInterval(this.ping);
      this.ping = null;
    }
    this.socket = null;
  }

  disconnect(): void {
    this.closed = true;
    this.retries = 0;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    const socket = this.socket;
    this.clear();
    try {
      socket?.close();
    } catch {
      /* already closed */
    }
  }
}
